import { useEffect, useState } from 'react';

import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import classNames from 'classnames';

import ArticleCard from '../ArticleCard';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  EditorialCrossLinkCardListProps,
  EditorialCrossLinkProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/EditorialCrossLinkProps';

import STYLES from './EditorialCrossLink.module.scss';

const getArticleSlug = (href: string) => {
  try {
    return (
      new URL(href, 'https://x').pathname.split('/').findLast(Boolean) ?? href
    );
  } catch {
    return href;
  }
};

const EditorialCrossLinkCardList = ({
  articles,
  aspectRatio,
}: EditorialCrossLinkCardListProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const rawComponentItemName = `${rawComponentName}_item`;
  return (
    <>
      {articles.map((article, index) => (
        <div
          className={STYLES.ArticleItem}
          key={article.articleHref}
          data-tracking-element-id={`${rawComponentItemName}_${index}`}
          data-tracking-common-params={buildMBDParam(rawComponentItemName, {
            article_slug: getArticleSlug(article.articleHref),
          })}
        >
          <ArticleCard
            key={article.articleHref}
            articleCreatedDate={article.articleCreatedDate}
            articleHref={article.articleHref}
            aspectRatio={aspectRatio}
            author={article.author}
            authorHref={article.authorHref}
            fallbackArticleCreatedLocalised={
              article.fallbackArticleCreatedLocalised
            }
            image={article.image}
            title={article.title}
            variant="editorialCrossLink"
          />
        </div>
      ))}
    </>
  );
};
const EditorialCrossLink = ({
  articles,
  header,
  isThreeCardLayout,
}: EditorialCrossLinkProps) => {
  const [isClient, setIsClient] = useState(false);
  const { buildMBDParam, rawComponentName } = useMBDContext();

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!articles?.length) {
    return null;
  }

  return (
    <div
      className={STYLES.EditorialCrossLink}
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <div className={STYLES.header}>
        <BpkSectionHeader title={header || ''} />
      </div>
      <BpkBreakpoint
        key={isClient ? 'client-key' : 'server-key'}
        query={BREAKPOINTS.SMALL_TABLET}
        matchSSR
      >
        {(isMobile: boolean) => {
          if (isMobile) {
            return (
              <div
                className={classNames([
                  STYLES.ArticlesScrollContainer,
                  {
                    [STYLES[`ArticlesScrollContainer--with-margin-top`]]:
                      header,
                  },
                ])}
              >
                <BpkMobileScrollContainer>
                  <div className={STYLES.ArticlesRow}>
                    <EditorialCrossLinkCardList
                      articles={articles}
                      aspectRatio={280 / 216}
                    />
                  </div>
                </BpkMobileScrollContainer>
              </div>
            );
          }

          return (
            <div
              className={classNames([
                STYLES.ArticlesGrid,
                {
                  [STYLES[`ArticlesGrid--with-margin-top`]]: header,
                  [STYLES[`ArticlesGrid--three-columns`]]: isThreeCardLayout,
                },
              ])}
            >
              <EditorialCrossLinkCardList
                articles={articles}
                aspectRatio={isThreeCardLayout ? 400 / 216 : 604 / 216}
              />
            </div>
          );
        }}
      </BpkBreakpoint>
    </div>
  );
};

export default EditorialCrossLink;
